<template>
  <div class="tabbed-code-snippets">
    <OptionsDropdown
      v-if="isProductLandingPage"
      :data="example"
      class="language-dropdown"
      @selected="onOptionSelection($event)"
    />
    <v-tabs connotation="primary" class="tabs">
      <v-tab
        v-for="(lang, index) in example"
        :key="index"
        :data-index="index"
        :icon="langIcon(lang)"
        :label="lang.title"
        :class="[{ 'tabs-section': isProductLandingPage }, 'tab']"
        @click="selectedLanguageIndex = index"
      />
      <VTabPanel
        v-for="(lang, index) in example"
        :key="lang.code + index.toString() + selectedLanguageIndex.toString()"
      >
        <div :id="selectedLanguageIndex">
          <div class="snippet" :style="{ 'max-height': isProductLandingPage ? '380px' : '250px' }">
            <code-snippet
              :lang="example[selectedLanguageIndex].prism_lang"
              :code="example[selectedLanguageIndex]?.code?.text"
            />
          </div>
          <div class="source-link">
            <ViewFullSourceLink
              v-if="example[selectedLanguageIndex]?.code?.link"
              :href="example[selectedLanguageIndex]?.code?.link"
            />
          </div>
        </div>
      </VTabPanel>
    </v-tabs>
  </div>
</template>
<script setup lang="ts">
import { PropType, ref } from 'vue';
import { VTabs, VTab, VTabPanel } from '@vonage/vivid-vue';
import { ExampleData, LanguageExample } from '@bifrost-dev-docs-mfe/common/types/examples';
import { CodeSnippet } from '@/components/markdown/widgets';
import ViewFullSourceLink from '@/components/example/components/ViewFullSourceLink.vue';
import OptionsDropdown from '@/components/composite/components/OptionsDropdown.vue';

const selectedLanguageIndex = ref(0);

defineProps({
  example: { type: Array as PropType<ExampleData>, required: true },
  isProductLandingPage: { type: Boolean, required: false },
});

function langIcon(lang: LanguageExample) {
  return lang.icon === 'curl' || lang.icon === 'ios' ? lang.colorlessIcon : lang.coloredIcon;
}

const onOptionSelection = (selectedIndex: number) => {
  selectedLanguageIndex.value = selectedIndex;
};
</script>
<style scoped lang="scss">
$background-color: var(--vvd-color-neutral-900);

.tabbed-code-snippets {
  background-color: $background-color;
  border-radius: 15px;
  overflow: hidden;

  /* This will unset tab color changes from vendor.css if applied any */
  *[aria-selected='true'] {
    --vvd-tabs-accent-primary: unset;
    --vvd-tab-accent-primary: unset;
    --vvd-tab-accent-faint: unset;
  }

  pre[class*='language-'] {
    padding: 10px;
    background-color: $background-color;

    code {
      background-color: $background-color !important;
      font-size: 15px;
    }
  }
  .tabs {
    --vvd-tab-accent-faint: rgba(255, 255, 255, 0.1);
    --vvd-tab-accent-soft: rgba(255, 255, 255, 0.1);
    --vvd-tab-accent-primary: var(--vvd-color-canvas);
  }
}
.snippet {
  overflow-y: auto;
  background-color: $background-color !important;
}

.source-link {
  padding: 10px 35px 24px;
  font-size: 14px;

  a {
    font: var(--vvd-typography-base-bold);
    color: var(--dp-source-link-color) !important;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
.language-dropdown {
  margin: 21px 0 20px 20px;
}

/* Extra small devices (phones, 600px and down) */
@media only screen and (max-width: $tablet-small) {
  .tabs-section {
    display: none;
  }
  .language-dropdown {
    display: inline-block;
  }
}

/* Small devices (portrait tablets and large phones, 600px and up) */
@media only screen and (min-width: $tablet-small) {
  .tabs-section {
    display: none;
  }
  .language-dropdown {
    display: inline-block;
  }
}

/* Medium devices (landscape tablets, 768px and up) */
@media only screen and (min-width: $tablet) {
  .tabs-section {
    display: none;
  }
  .language-dropdown {
    display: inline-block;
  }
}

/* Large devices (laptops/desktops, 992px and up) */
@media only screen and (min-width: $laptop) {
  .tabs-section {
    display: block;
  }
  .language-dropdown {
    display: none;
  }
}
.tab {
  background-color: $background-color;
}
</style>
